import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../i18n'
import { setLanguage, LANGUAGES } from '../i18n'
import { useAuth } from '../context'
import { PortalPage } from '../components/portal/PortalPage'
import { TRANSITION_PRESS } from '../lib/motion'

const s: Record<string, React.CSSProperties> = {
  header: {
    background: 'rgba(255,255,255,0.04)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    justifyContent: 'space-between',
  },
  greeting: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '55%',
  },
  actions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  iconBtn: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: 'none',
    background: 'rgba(255,255,255,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.7)',
    position: 'relative' as const,
    transition: TRANSITION_PRESS,
  },
  langDropdown: {
    position: 'absolute' as const,
    top: 48,
    right: 0,
    background: '#1a2029',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 12,
    padding: 8,
    zIndex: 100,
    minWidth: 160,
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  langItem: {
    padding: '10px 14px',
    borderRadius: 8,
    color: 'rgba(255,255,255,0.7)',
    fontSize: 14,
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left' as const,
    display: 'block',
  },
  langItemActive: {
    background: 'rgba(255,107,53,0.15)',
    color: '#ff6b35',
  },
}

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
)

const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

const STAFF_FLOW_WITHOUT_GREETING = /^\/staff\/(capture|submit|scan|select|cycles)(\/|$)/

export function StaffApp({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation()
  const { profile, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const hideShellHeader = STAFF_FLOW_WITHOUT_GREETING.test(pathname)
  const [showLang, setShowLang] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowLang(false)
      }
    }
    if (showLang) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showLang])

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const handleLangChange = (code: string) => {
    void (async () => {
      await setLanguage(code)
      setShowLang(false)
    })()
  }

  const name = profile?.full_name || t('staff', 'Staff')

  return (
    <div className="staff-app-shell portal-shell" data-chrome={hideShellHeader ? 'off' : 'on'}>
      {!hideShellHeader && (
        <div className="portal-app-header" style={s.header}>
          <div style={s.greeting}>
            {t('home_hello', 'Hello, {{name}}', { name })}
          </div>
          <div style={s.actions}>
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <button style={s.iconBtn} onClick={() => setShowLang(!showLang)} aria-label={t('lang_select', 'Language')}>
                <GlobeIcon />
              </button>
              {showLang && (
                <div style={s.langDropdown}>
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      style={{
                        ...s.langItem,
                        ...(i18n.language?.toLowerCase().startsWith(lang.code) ? s.langItemActive : {}),
                      }}
                      onClick={() => handleLangChange(lang.code)}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button style={s.iconBtn} onClick={handleLogout} aria-label={t('home_logout', 'Logout')}>
              <LogoutIcon />
            </button>
          </div>
        </div>
      )}
      <div className="staff-app-content">
        <PortalPage>{children}</PortalPage>
      </div>
    </div>
  )
}
